import React, { useContext } from "react";
import styled, { useTheme } from "styled-components";
import { useSelector } from "react-redux";
import { Layers, LucideIcon, Scale, TrendingUp } from "lucide-react";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { useDashboardStatistics } from "@shared/features/dashboard/hooks";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { useTotalPnl } from "@shared/features/socket/hooks";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import { DISPLAY_CURRENCIES } from "common/constants";
import { STAT_STRIP_ID } from "testIds";
import { getNewLookTokens, newLookPhoneType } from "common/NewLook/tokens.ts";
import { phoneCardShadow, PhoneSkeleton } from "./PhoneParts";
import AutoSizeText from "common/Text/AutoSizeText.tsx";
import { formatCompactAmount } from "features/watchlist/AllMarkets/NewLookMarketCells.tsx";

const COMPACT_FROM = 1_000_000_000;

/** Three figure tiles that overlap the blue header's bottom edge. */
const PhoneTiles = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const theme = useTheme();
  const displayCurrency = useSelector(selectDisplayCurrency);
  const symbol = displayCurrency === DISPLAY_CURRENCIES.USD ? "$" : "₿";
  const { getDisplayCurrencyAmountPrecision } = usePrecisions();
  const precision = getDisplayCurrencyAmountPrecision();
  const statistics = useDashboardStatistics(false, true);
  const { socketPnlValue } = useContext(SocketPnlContext)!;
  const pnl = useTotalPnl(statistics.openTradesData, socketPnlValue);

  const sign = (value: number, signed: boolean) =>
    value < 0 ? "−" : signed && value > 0 ? "+" : "";
  const fullMoney = (value: number, signed = false) =>
    `${sign(value, signed)}${symbol}${getLocaleStringWithPrecision(
      Math.abs(value),
      precision
    )}`;
  const money = (value: number, signed = false) =>
    Math.abs(value) >= COMPACT_FROM
      ? `${sign(value, signed)}${formatCompactAmount(Math.abs(value), symbol)}`
      : fullMoney(value, signed);
  let pnlColor: string | undefined;
  if (pnl > 0) pnlColor = theme.colors.green;
  else if (pnl < 0) pnlColor = theme.colors.red;

  const tiles: {
    key: string;
    icon: LucideIcon;
    label: string;
    value: string;
    fullValue: string;
    color?: string;
  }[] = [
    {
      key: "in-trades",
      icon: Layers,
      label: t("phone.in_open_trades"),
      value: money(statistics.tradesAmount),
      fullValue: fullMoney(statistics.tradesAmount),
    },
    {
      key: "pnl",
      icon: Scale,
      label: t("phone.open_pnl"),
      value: money(pnl, true),
      fullValue: fullMoney(pnl, true),
      color: pnlColor,
    },
    {
      key: "potential",
      icon: TrendingUp,
      label: t("statistics.potential_profit"),
      value: money(statistics.tradesPotentialProfit),
      fullValue: fullMoney(statistics.tradesPotentialProfit),
    },
  ];

  return (
    <Tiles data-testid={STAT_STRIP_ID}>
      {tiles.map(({ key, icon: Icon, label, value, fullValue, color }) => (
        <Tile key={key}>
          <IconBox>
            <Icon size={15} strokeWidth={2} />
          </IconBox>
          <Label>{label}</Label>
          {statistics.isLoading ? (
            <PhoneSkeleton
              aria-busy="true"
              $width={64}
              $height={16}
              style={{ marginTop: 3 }}
            />
          ) : (
            <Value style={color ? { color } : undefined}>
              <AutoSizeText inheritFont={true} title={fullValue}>
                {value}
              </AutoSizeText>
            </Value>
          )}
        </Tile>
      ))}
    </Tiles>
  );
};

export default React.memo(PhoneTiles);

const Tiles = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 16px 0;
`;
const Tile = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 12px;
  border-radius: ${(p) => p.theme.radii.card}px;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${phoneCardShadow};
`;
const IconBox = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-bottom: 8px;
  border-radius: 8px;
  color: ${(p) => p.theme.colors.primaryBlue};
  background: ${(p) => p.theme.colors.lightBlue};
`;
const Label = styled.div`
  ${newLookPhoneType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  @media (max-width: 374px) {
    font-size: 11px;
    line-height: 14px;
    white-space: normal;
  }
`;
const Value = styled.div`
  margin-top: auto;
  padding-top: 2px;
  ${newLookPhoneType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
